import React, { useState, useMemo } from "react";
import PositionInput from "./PositionInput";
import AnnotationEntry from "./AnnotationEntry";
import AddButton from "./AddButton";
import AnnotationModal from "./AnnotationModal";
import { useAnnotation } from "../contexts/AnnotationContext";
import { IconButton, Collapse } from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { useViewMode } from "../contexts/ViewModeContext";
import { calculateMaxSubdivisions, calculateMaxBeats, getTimeSignatureForBar } from "../utils/subdivisionUtils";
import InfoTooltip from "./InfoTooltip";
import { TempoInstruction } from "../lib/definitions/SongLevelAnnotation";

/**
 * TempoChangeSection component for adding and editing tempo changes
 * Updated to be collapsible and take less space
 */
const TempoChangeSection = ({ 
  tempoChanges, 
  onChange,
}) => {

  const { 
    generalInfo,
    timeSignatures,
  } = useAnnotation();
  const measures = generalInfo.duration_number_of_bars;

  const [showForm, setShowForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [newTempoChange, setNewTempoChange] = useState({
    position: { bar: 1, beat: 1, subdivision: 1 },
    value: 120
  });
  const [error, setError] = useState("");

  // View mode
  const {isViewMode} = useViewMode(); 

  // Calculate max subdivisions based on current bar's time signature
  const maxSubdivision = useMemo(() => {
    const currentBar = newTempoChange.position.bar;
    if (!currentBar || isNaN(currentBar)) return 4; // Default fallback
    
    const timeSignature = getTimeSignatureForBar(currentBar, timeSignatures);
    return calculateMaxSubdivisions(timeSignature);
  }, [newTempoChange.position.bar, timeSignatures]);

  // Calculate max beats based on current bar's time signature
  const maxBeat = useMemo(() => {
    const currentBar = newTempoChange.position.bar;
    if (!currentBar || isNaN(currentBar)) return 4; // Default fallback
    
    const timeSignature = getTimeSignatureForBar(currentBar, timeSignatures);
    return calculateMaxBeats(timeSignature);
  }, [newTempoChange.position.bar, timeSignatures]);

  const handleAddClick = () => {
    setNewTempoChange({
      position: { bar: 1, beat: 1, subdivision: 1 },
      value: 120
    });
    setEditingIndex(null);
    setShowForm(true);
    setExpanded(true); // Auto-expand when adding
  };

  const handleEditClick = (index) => {
    const tempoChange = tempoChanges[index];
    setNewTempoChange({
      position: { ...tempoChange.position },
      value: tempoChange.value
    });
    setEditingIndex(index);
    setShowForm(true);
    setExpanded(true); // Auto-expand when editing
  };

  const handlePositionChange = (position) => {
    setNewTempoChange({ ...newTempoChange, position });
  };

  const handleTempoChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value > 0) {
      setNewTempoChange({ ...newTempoChange, value });
    }
  };

  const handleSave = () => {
    // Clear previous errors
    setError("");
    
    // COMMENTED OUT: Measure range validation
    // Convert measure to number and validate
    // console.log("handling save ", newTempoChange.position.bar)
    // const measureNum = newTempoChange.position.bar;
    // if (isNaN(measureNum) || measureNum < 1 || measureNum > measures) {
    //   setError(`Please enter a valid measure number between 1 and ${measures}`);
    //   return;
    // }

    // COMMENTED OUT: Beat validation based on time signature
    // Check correct beat number for time signatures
    // const timeSig = getTimeSignatureForBar(measureNum, timeSignatures);
    // const beatsInBar = parseInt(timeSig.split("/")[0], 10);   // numerator
    // const beatNum   = newTempoChange.position.beat;
  
    // if (isNaN(beatNum) || beatNum < 1 || beatNum > beatsInBar) {
    //   setError(
    //     `Beat must be between 1 and ${beatsInBar} for bar ${measureNum} (time signature ${timeSig}).`
    //   );
    //   return;
    // }

    // COMMENTED OUT: Subdivision validation based on time signature
    // Check subdivision limits based on time signature
    // const maxSubdivisionsForBar = calculateMaxSubdivisions(timeSig);
    // const subdivisionNum = newTempoChange.position.subdivision;
    
    // if (isNaN(subdivisionNum) || subdivisionNum < 1 || subdivisionNum > maxSubdivisionsForBar) {
    //   setError(
    //     `Subdivision must be between 1 and ${maxSubdivisionsForBar} for bar ${measureNum} (time signature ${timeSig}).`
    //   );
    //   return;
    // }
  
    // COMMENTED OUT: Chronological order validation
    // Make sure new changes are in chronological order
    // const posToNumber = ({ bar, beat, subdivision }) =>
    //   bar * 10_000 + beat * 100 + subdivision;   // assumes <100 beats & <100 subdivisions

    // const newPos = posToNumber(newTempoChange.position);

    // const latestPos = tempoChanges
    //   .filter((_, idx) => idx !== editingIndex)
    //   .reduce(
    //     (max, tc) => Math.max(max, posToNumber(tc.position)),
    //     0
    //   );

    // if (newPos <= latestPos) {
    //   setError(
    //     "Tempo changes must be added in chronologically increasing order."
    //   );
    //   return;
    // }
  
    // COMMENTED OUT: BPM range warning
    // Warn if tempo is unusually slow or fast
    // if (newTempoChange.value < 40 || newTempoChange.value > 200) {
    //   const proceed = window.confirm(
    //     `${newTempoChange.value} BPM is outside the usual 40‑200 BPM range. Save anyway?`
    //   );
    //   if (!proceed) return;
    // }

    const updated = [...tempoChanges];
    if (editingIndex != null) updated[editingIndex] = newTempoChange;
    else updated.push(newTempoChange);
    updated.sort((a,b) => a.position.bar - b.position.bar);
    onChange(updated);
    
    // Close form if successful
    setShowForm(false);
  };

  const handleCancel = () => {
    setShowForm(false);
  };

  const handleDelete = (index) => {
    const updated = tempoChanges.filter((_,i) => i !== index);
    onChange(updated);
  };

  const formatPosition = (pos) => {
    return `${pos.bar}:${pos.beat}:${pos.subdivision}`;
  };

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  // Get a summary of tempo changes for the collapsed view
  const getSummary = () => {
    if (tempoChanges.length === 0) {
      return "No tempo changes";
    } else if (tempoChanges.length === 1) {
      const tc = tempoChanges[0];
      return `1 tempo change: ${tc.value} BPM at ${formatPosition(tc.position)}`;
    } else {
      return `${tempoChanges.length} tempo changes`;
    }
  };

  return (
    <div className="mb-4 rounded-md overflow-hidden">
      {/* Header with expand/collapse */}
      <div 
        className="flex items-center px-3 py-2 bg-gray-50 cursor-pointer"
        onClick={toggleExpand}
      >
        <h3 className="text-base font-medium m-0"> Tempo (曲速) </h3>
        <div className="flex items-center ml-auto">
          <span className="text-sm text-gray-500 mr-2">{!expanded && getSummary()}</span>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleExpand(); }}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </div>
        <InfoTooltip instruction={TempoInstruction}/>
      </div>
      
      {/* Collapsible content */}
      <Collapse in={expanded}>
        <div className="px-3 py-2">
          {/* List of existing tempo changes */}
          <div className="flex flex-wrap items-center">
            {tempoChanges.map((tempoChange, index) => (
              <div key={index} className="mr-2 mb-2">
                <AnnotationEntry
                  label={`${formatPosition(tempoChange.position)} ${tempoChange.value} BPM`}
                  onDelete={() => handleDelete(index)}
                  onClick={() => handleEditClick(index)}
                />
              </div>
            ))}
            {!isViewMode && <AddButton onClick={handleAddClick} />}
          </div>
        </div>
      </Collapse>
      
      {/* Modal for adding/editing */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <AnnotationModal
            title="Tempo (曲速)"
            onSave={handleSave}
            onCancel={handleCancel}
            isEditing={editingIndex !== null}
            error={error}
          >
            <div className="mb-4">
              <div className="mb-3">
                <label className="block text-sm mb-1">Position</label>
                <PositionInput 
                  position={newTempoChange.position} 
                  onChange={handlePositionChange}
                  maxSubdivision={maxSubdivision}
                  maxBeat={maxBeat}
                />
              </div>
              
              <div className="mb-3">
                <label className="block text-sm mb-1">Tempo (BPM)</label>
                <input
                  type="number"
                  min="1"
                  value={newTempoChange.value}
                  onChange={handleTempoChange}
                  className="w-32 px-3 py-2 bg-gray-200 rounded-md"
                />
              </div>
            </div>
          </AnnotationModal>
        </div>
      )}
    </div>
  );
};

export default TempoChangeSection;
